import api from "../../../services/api";

/** GET /api/v1/account/pets */
export async function listMyPets() {
    const { data } = await api.get("/api/v1/account/pets");
    return data;
}

export async function listMyReports(signal) {
    const { data } = await api.get("/api/v1/account/pet_reports", { signal });
    return Array.isArray(data) ? data : (data?.items || []);
}

export async function listPetReports({
    type,
    species,
    municipalityPlaceId,
    municipality,
    eventOccurredAtGteq,
    eventOccurredAtLteq,
    page = 1,
    perPage = 50,
    signal,
} = {}) {
    const params = {
        page,
        per_page: perPage,
    };

    if (type && type !== "all") {
        params["q[report_type_eq]"] = type;
    }

    if (species && species !== "all") {
        params["q[species_eq]"] = species;
    }

    if (municipalityPlaceId) {
        params["q[municipality_place_id_eq]"] = municipalityPlaceId;
    } else if (municipality) {
        params["q[municipality_eq]"] = municipality;
    }

    if (eventOccurredAtGteq) {
        params["q[event_occurred_at_gteq]"] = eventOccurredAtGteq;
    }
    if (eventOccurredAtLteq) {
        params["q[event_occurred_at_lteq]"] = eventOccurredAtLteq;
    }

    const { data } = await api.get("/api/v1/pet_reports", {
        params,
        signal,
    });

    const items = Array.isArray(data?.items) ? data.items : [];
    const pagy = data?.pagy || null;

    return { items, pagy };
}

export async function listMunicipalities({
    q,
    limit,
    page = 1,
    perPage = 50,
    signal,
    countryCode,
    reportType,
    sort,
} = {}) {
    const params = {};

    if (q && q.trim()) {
        params.q = q.trim();
        if (limit) params.limit = limit;
    } else {
        params.page = page;
        params.per_page = perPage;
    }

    if (countryCode) params.country_code = countryCode;
    if (reportType && reportType !== "all") params.report_type = reportType;
    if (sort) params.sort = sort;

    const { data } = await api.get("/api/v1/municipalities", { params, signal });

    const items = Array.isArray(data?.items) ? data.items : [];
    const pagy = data?.pagy || null;

    return { items, pagy };
}
export async function createMyPet({ name, photoFile, photoDataUrl }) {
    // 1) Con archivo → multipart/form-data
    if (photoFile) {
        const form = new FormData();
        form.append("pet[name]", name);
        // ⚠️ Ajusta este key según tu backend: "pet[photo]" / "pet[image]" / etc.
        form.append("pet[profile_image]", photoFile);

        const { data, status } = await api.post("/api/v1/account/pets", form, {
            headers: { "Content-Type": "multipart/form-data" },
        });
        return { data, status };
    }

    const payload = {
        pet: {
            name,
            ...(photoDataUrl ? { photo_data: photoDataUrl } : {}),
        },
    };

    const { data, status } = await api.post("/api/v1/account/pets", payload);
    return { data, status };
}

export async function validateGuestImage(photoFile) {
    const form = new FormData();
    form.append("photo", photoFile);

    // This endpoint is public and does not require a token
    const { data, status } = await api.post("/api/v1/public/validate_image", form, {
        headers: { "Content-Type": "multipart/form-data" },
        // Ensure that we don't attach the token if api instance automatically does it,
        // but typically public endpoints just ignore the token or it's fine.
        // If the api instance intercepts and adds a token, it might be okay.
        // If we strictly need no token, we might need a separate instance or config.
        // Assuming current api instance handles it or it's acceptable.
    });
    return { data, status };
}

export async function getMyPet(id, { signal } = {}) {
    const { data } = await api.get(`/api/v1/account/pets/${id}`, { signal });
    return data?.pet || data;
}

export async function getPublicPet(id, { signal } = {}) {
    const { data } = await api.get(`/api/v1/pets/${id}`, { signal });
    return data?.pet || data;
}

export async function getPetReport(id, { signal } = {}) {
    const { data } = await api.get(`/api/v1/account/pet_reports/${id}`, { signal });
    // Assuming backend returns { pet_report: {...} } or just {...}
    return data?.pet_report || data;
}

export async function updateMyPet(
    id,
    petAttrs,
    { photoFile, signal } = {}
) {
    // Pet attrs esperados (todos opcionales):
    // name, chip_number, description, birthdate, approximated_age, gender, neutered, breed_id

    if (photoFile) {
        // multipart/form-data
        const form = new FormData();

        // helper para no enviar null/undefined
        const appendIf = (k, v) => {
            if (v !== undefined && v !== null && v !== "") form.append(`pet[${k}]`, v);
        };

        // Atributos enviados como campos normales
        appendIf("name", petAttrs?.name);
        appendIf("chip_number", petAttrs?.chip_number);
        appendIf("description", petAttrs?.description);
        appendIf("birthdate", petAttrs?.birthdate);
        appendIf("approximated_age", typeof petAttrs?.approximated_age === "boolean" ? String(petAttrs?.approximated_age) : undefined);
        appendIf("gender", petAttrs?.gender);
        appendIf("neutered", typeof petAttrs?.neutered === "boolean" ? String(petAttrs?.neutered) : undefined);
        appendIf("breed_id", petAttrs?.breed_id);

        // archivo
        form.append("pet[profile_image]", photoFile);

        const { data } = await api.patch(`/api/v1/account/pets/${id}`, form, {
            signal,
            headers: { "Content-Type": "multipart/form-data" },
        });
        return data?.pet || data;
    }

    // JSON (tu caso del cURL)
    const payload = { pet: petAttrs };
    const { data } = await api.patch(`/api/v1/account/pets/${id}`, payload, { signal });
    return data?.pet || data;
}

export async function getBreeds({ q = {}, signal } = {}) {
    const params = new URLSearchParams();
    Object.entries(q).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== "") params.append(`q[${k}]`, v);
    });

    const qs = params.toString() ? `?${params.toString()}` : "";
    const { data } = await api.get(`/api/v1/breeds${qs}`, { signal });
    return Array.isArray(data) ? data : (data?.breeds || data?.items || []);
}

export async function saveCareProfile(petId, careProfile, { signal } = {}) {
    const payload = { care_profile: careProfile };
    const { data } = await api.post(
        `/api/v1/account/pets/${petId}/care_profile`,
        payload,
        { signal }
    );
    // ajusta el retorno si tu API envuelve distinto
    return data?.care_profile || data;
}

// getCarePlanTasks.js
export async function getCarePlan(petId, planId, { signal } = {}) {
    const { data } = await api.get(
        `/api/v1/account/pets/${petId}/care_plans/${planId}`,
        { signal }
    );
    return data;
}

export async function getCarePlanTasks(
    petId,
    planId,
    { days = 5, sort = 'start_at asc', signal } = {}
) {
    const start = new Date();
    start.setHours(0, 0, 0, 0);

    const end = new Date(start);
    end.setDate(end.getDate() + days);
    end.setHours(23, 59, 59, 999);

    const params = {
        'q[start_at_gteq]': start.toISOString(),
        'q[start_at_lteq]': end.toISOString(),
        'q[s]': sort,
    };

    const { data } = await api.get(
        `/api/v1/account/pets/${petId}/care_plans/${planId}/tasks`,
        { params, signal }
    );

    return data;
}

export async function getAllTasks(
    { days = 2, sort = 'start_at asc', signal } = {}
) {
    const start = new Date();
    start.setHours(0, 0, 0, 0);

    const end = new Date(start);
    end.setDate(end.getDate() + days);
    end.setHours(23, 59, 59, 999);

    const params = {
        'q[start_at_gteq]': start.toISOString(),
        'q[start_at_lteq]': end.toISOString(),
        'q[s]': sort,
    };

    const { data } = await api.get(
        `/api/v1/account/pets/all_tasks`,
        { params, signal }
    );

    return data;
}

export async function createCarePlanForPet(petId, body) {
    const res = await api.post(`/api/v1/account/pets/${petId}/care_plans`, body);
    return { data: res.data, status: res.status };
}

export async function createHealthCheck(petId, { health_check_type, notes, images = [] }) {
    const formData = new FormData();
    formData.append("health_check[health_check_type]", health_check_type);
    if (notes) {
        formData.append("notes", notes);
    }
    images.forEach((file) => {
        if (file) formData.append("images[]", file);
    });

    const res = await api.post(`/api/v1/account/pets/${petId}/health_checks`, formData, {
        headers: {
            "Content-Type": "multipart/form-data",
        },
    });
    return { data: res.data, status: res.status };
}

export async function getCarePlanTask(petId, planId, taskId, { signal } = {}) {
    const { data } = await api.get(
        `/api/v1/account/pets/${petId}/care_plans/${planId}/tasks/${taskId}`,
        { signal }
    );
    return data;
}

// Marcar una tarea como COMPLETED
export async function completeCarePlanTask(petId, carePlanId, taskId, { signal } = {}) {
    const { data } = await api.post(
        `/api/v1/account/pets/${petId}/care_plans/${carePlanId}/tasks/${taskId}/complete`,
        {},
        { signal }
    );

    return data?.task || data;
}

// Marcar una tarea como SKIPPED
export async function skipCarePlanTask(petId, carePlanId, taskId, { signal } = {}) {
    const { data } = await api.post(
        `/api/v1/account/pets/${petId}/care_plans/${carePlanId}/tasks/${taskId}/skip`,
        {},
        { signal }
    );

    return data?.task || data;
}

// Actualizar una tarea de un care plan
export async function updateCarePlanTask(
    petId,
    carePlanId,
    taskId,
    taskAttrs,
    { signal } = {}
) {
    // taskAttrs puede incluir:
    // { category, title, start_at, duration_min, notes, status, locked, recurrence_rule, ... }

    const payload = {
        task: taskAttrs,
    };

    const { data } = await api.patch(
        `/api/v1/account/pets/${petId}/care_plans/${carePlanId}/tasks/${taskId}`,
        payload,
        { signal }
    );

    return data?.task || data;
}

// Crear una nueva tarea en un care plan
export async function createCarePlanTask(
    petId,
    carePlanId,
    taskAttrs,
    { signal } = {}
) {
    // taskAttrs: { category, title, start_at, duration_min, notes, status, locked, ... }
    const payload = { task: taskAttrs };

    const { data } = await api.post(
        `/api/v1/account/pets/${petId}/care_plans/${carePlanId}/tasks`,
        payload,
        { signal }
    );

    return data?.task || data;
}

export async function createHealthRecord(petId, attrs, { signal } = {}) {
    const payload = { health_record: attrs };

    const { data } = await api.post(
        `/api/v1/account/pets/${petId}/health_records`,
        payload,
        { signal }
    );

    return data?.health_record || data;
}

export async function updateHealthRecord(petId, recordId, attrs, { signal } = {}) {
    const payload = { health_record: attrs };

    const { data } = await api.patch(
        `/api/v1/account/pets/${petId}/health_records/${recordId}`,
        payload,
        { signal }
    );

    return data?.health_record || data;
}

export async function attachHealthRecordDocument(petId, recordId, file, { signal } = {}) {
    const formData = new FormData();
    formData.append("document", file);

    const { data } = await api.post(
        `/api/v1/account/pets/${petId}/health_records/${recordId}/attach_document`,
        formData,
        {
            signal,
            // clave: NO lo transformes a JSON y deja que axios/browsers manejen el Content-Type
            headers: {
                // opcional: si tu instancia api mete application/json por defecto,
                // esto la pisa y permite multipart/form-data
                "Content-Type": "multipart/form-data",
            },
            transformRequest: [(formData) => formData],
        }
    );

    return data?.health_record || data;
}
export async function removeHealthRecordDocument(petId, recordId, attachmentId, { signal } = {}) {
    const payload = { attachment_id: attachmentId };

    const { data } = await api.delete(
        `/api/v1/account/pets/${petId}/health_records/${recordId}/remove_document`,
        {
            data: payload,
            signal
        }
    );

    return data?.health_record || data;
}

export async function downloadHealthRecordDocument(url, filename = "document") {
    const { data } = await api.get(url, {
        responseType: "blob"
    });

    const blob = new Blob([data]);
    const link = document.createElement("a");
    const objectUrl = URL.createObjectURL(blob);

    link.href = objectUrl;
    link.download = filename;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(objectUrl);
}

// src/features/pets/data/pets.service.js

export async function createLostPetReport({
    petId,
    petName, // Added
    eventOccurredAt,
    locationLat,
    locationLng,
    locationDescription,
    userDescription,
    photos = [],
    locationMunicipality,
    locationCountry,
    locationMunicipalityPlaceId,
    locationMunicipalitySlug,
    locationCountryCode,
    contactName,
    contactNumber,
    contactVk,
    vkCoauthor,
}) {
    const formData = new FormData();
    if (petId) formData.append("pet_id", String(petId));
    if (petName) formData.append("pet_name", petName);

    formData.append("event_occurred_at", eventOccurredAt);
    formData.append("location_lat", String(locationLat));
    formData.append("location_lng", String(locationLng));
    formData.append("location_description", locationDescription || "");
    formData.append("user_description", userDescription || "");

    if (contactName) formData.append("contact_name", contactName);
    if (contactNumber) formData.append("contact_number", contactNumber);
    if (contactVk) formData.append("contact_vk", contactVk);
    if (vkCoauthor !== undefined) formData.append("vk_coauthor", String(vkCoauthor));

    if (locationMunicipality) {
        formData.append("municipality", locationMunicipality);
    }
    if (locationMunicipalityPlaceId) {
        formData.append("municipality_place_id", locationMunicipalityPlaceId);
    }
    if (locationMunicipalitySlug !== undefined && locationMunicipalitySlug !== null) {
        formData.append("municipality_slug", locationMunicipalitySlug);
    }

    if (locationCountryCode) {
        formData.append("country", locationCountryCode);
    }

    photos.forEach((file) => {
        if (file) formData.append("photos[]", file);
    });

    return api.post("/api/v1/account/pet_reports/lost", formData, {
        headers: { "Content-Type": "multipart/form-data" },
    });
}

export async function createFoundPetReport({
    eventOccurredAt,        // string ISO
    locationLat,
    locationLng,
    locationDescription,
    userDescription,
    photos = [],            // array<File>
    locationMunicipality,   // nombre legible
    locationCountry,        // nombre legible
    // nuevos:
    locationMunicipalityPlaceId,
    locationMunicipalitySlug,
    locationCountryCode,
    contactName,
    contactNumber,
    contactVk,
    vkCoauthor,
}) {
    const formData = new FormData();

    formData.append("event_occurred_at", eventOccurredAt);
    formData.append("location_lat", String(locationLat));
    formData.append("location_lng", String(locationLng));
    formData.append("location_description", locationDescription || "");
    formData.append("user_description", userDescription || "");

    if (contactName) formData.append("contact_name", contactName);
    if (contactNumber) formData.append("contact_number", contactNumber);
    if (contactVk) formData.append("contact_vk", contactVk);
    if (vkCoauthor !== undefined) formData.append("vk_coauthor", String(vkCoauthor));

    // campos existentes
    if (locationMunicipality) {
        formData.append("municipality", locationMunicipality);
    }
    if (locationMunicipalityPlaceId) {
        formData.append("municipality_place_id", locationMunicipalityPlaceId);
    }
    if (locationMunicipalitySlug !== undefined && locationMunicipalitySlug !== null) {
        formData.append("municipality_slug", locationMunicipalitySlug);
    }

    if (locationCountryCode) {
        formData.append("country", locationCountryCode);
    }

    photos.forEach((file) => {
        if (file) formData.append("photos[]", file);
    });

    return api.post("/api/v1/account/pet_reports/found", formData, {
        headers: { "Content-Type": "multipart/form-data" },
    });
}

export async function getPetReportMatchesByReportId(sourceReportId, { signal } = {}) {
    const { data } = await api.get(
        `/api/v1/pet_reports/${sourceReportId}/matches`,
        { signal }
    );

    if (Array.isArray(data)) return data;
    if (Array.isArray(data.matches)) return data.matches;
    if (Array.isArray(data.items)) return data.items;

    return data;
}

/** PATCH /api/v1/account/pet_reports/:id */
export async function updatePetReport(id, petReportAttrs) {
    const payload = {
        pet_report: petReportAttrs
    };
    const { data } = await api.patch(`/api/v1/account/pet_reports/${id}`, payload);
    return data;
}

/** POST /api/v1/account/pet_reports/:id/attach_photo */
export async function attachPhotoToReport(id, signedBlobId) {
    const { data } = await api.post(
        `/api/v1/account/pet_reports/${id}/attach_photo`,
        { signed_blob_id: signedBlobId }
    );
    return data;
}

/** DELETE /api/v1/account/pet_reports/:id/remove_photo */
export async function removePhotoFromReport(id, attachmentId) {
    const { data } = await api.delete(
        `/api/v1/account/pet_reports/${id}/remove_photo`,
        {
            params: { photo_id: attachmentId }
        }
    );
    return data;
}

/** POST /api/v1/account/pet_reports/:id/process */
export async function processPetReport(id) {
    const { data } = await api.post(`/api/v1/account/pet_reports/${id}/process`);
    return data;
}

export async function deletePetReport(id) {
    const { data } = await api.delete(`/api/v1/account/pet_reports/${id}`);
    return data;
}

export async function getMunicipalityUsers(placeId, { signal } = {}) {
    const { data } = await api.get(`/api/v1/municipalities/${placeId}/users`, { signal });
    return data;
} export async function createReportSignal(petReportId, { signalType, text }) {
    const payload = {
        signal: {
            signal_type: signalType,
            text: text || null
        }
    };
    const { data } = await api.post(`/api/v1/pet_reports/${petReportId}/signals`, payload);
    return data;
}

/** POST /api/v1/pet_reports/:id/activate_matching */
export async function activateMatching(id) {
    const { data } = await api.post(`/api/v1/pet_reports/${id}/activate_matching`);
    return data;
}
